import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { api } from "@/lib/api";

export const OFFERINGS = [
  { value: "private", label: "Private 1:1 — Transformational Work" },
  { value: "corporate", label: "Corporate Counselling — Workshops & Leadership" },
  { value: "meditation", label: "Meditation — Guided Audio & Digital" },
  { value: "community", label: "Community & Online Circles" },
  { value: "events", label: "Events & Offline Experiences" },
  { value: "other", label: "Something else" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls =
  "w-full bg-transparent border-b border-[rgba(169,129,47,0.26)] focus:border-gold py-3 text-ink placeholder:text-inkdim/40 outline-none transition-colors duration-300 text-[15px]";

const labelCls = "block text-[10px] uppercase tracking-[0.3em] text-inkdim mb-1";

export default function ContactForm() {
  const [params] = useSearchParams();
  const initial = OFFERINGS.some((o) => o.value === params.get("offering")) ? params.get("offering") : "private";
  const [form, setForm] = useState({ name: "", email: "", offering: initial, message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Your name, so Rajdeep knows who's writing.";
    if (!EMAIL_RE.test(form.email)) errs.email = "A valid email, so the reply can find you.";
    if (form.message.trim().length < 4) errs.message = "A few words about what's bringing you here.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("sending");
    try {
      await api.post("/contact", form);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div data-testid="contact-form-wrapper">
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="border border-[rgba(169,129,47,0.26)] bg-sand/40 px-8 py-14 text-center"
            data-testid="contact-success-message"
          >
            <span className="inline-flex w-11 h-11 rounded-full border border-gold/50 items-center justify-center text-gold mb-6">
              <Check size={18} strokeWidth={1.5} />
            </span>
            <p className="font-display text-2xl text-goldink mb-3">Your words are on their way.</p>
            <p className="text-inkdim text-sm leading-relaxed max-w-sm mx-auto">
              Rajdeep reads every message herself. Expect a considered reply — not a template.
            </p>
            <button
              onClick={() => {
                setForm({ name: "", email: "", offering: "private", message: "" });
                setStatus("idle");
              }}
              className="gold-underline-link mt-7 text-[11px] uppercase tracking-[0.28em] text-gold"
              data-testid="contact-send-another-button"
            >
              Write another message
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={submit}
            noValidate
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-8"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label htmlFor="cf-name" className={labelCls}>Name</label>
                <input
                  id="cf-name"
                  type="text"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  className={inputCls}
                  data-testid="contact-name-input"
                />
                {errors.name && <p className="text-xs text-[#9E3B1F] mt-2" data-testid="contact-name-error">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="cf-email" className={labelCls}>Email</label>
                <input
                  id="cf-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={inputCls}
                  data-testid="contact-email-input"
                />
                {errors.email && <p className="text-xs text-[#9E3B1F] mt-2" data-testid="contact-email-error">{errors.email}</p>}
              </div>
            </div>
            <div>
              <label htmlFor="cf-offering" className={labelCls}>Which conversation are you starting?</label>
              <select
                id="cf-offering"
                value={form.offering}
                onChange={set("offering")}
                className={`${inputCls} cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2210%22 height=%226%22%3E%3Cpath d=%22M1 1l4 4 4-4%22 stroke=%22%23C9A24B%22 fill=%22none%22/%3E%3C/svg%3E')] bg-no-repeat bg-[right_0.25rem_center]`}
                data-testid="contact-offering-select"
              >
                {OFFERINGS.map((o) => (
                  <option key={o.value} value={o.value} className="bg-sand text-ink">
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="cf-message" className={labelCls}>What's bringing you here?</label>
              <textarea
                id="cf-message"
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="The loop you keep finding yourself in. The question you can't put down."
                className={`${inputCls} resize-none`}
                data-testid="contact-message-textarea"
              />
              {errors.message && <p className="text-xs text-[#9E3B1F] mt-2" data-testid="contact-message-error">{errors.message}</p>}
            </div>
            {status === "error" && (
              <p className="text-sm text-[#9E3B1F]" data-testid="contact-error-message">
                Something interrupted the send. Your words weren't lost — please try once more.
              </p>
            )}
            <button
              type="submit"
              disabled={status === "sending"}
              className="bg-deep text-canvas text-xs uppercase tracking-[0.28em] font-semibold px-10 py-4 hover:bg-gold transition-colors duration-300 disabled:opacity-60 disabled:cursor-wait"
              data-testid="contact-submit-button"
            >
              {status === "sending" ? "Sending…" : "Send it to Rajdeep"}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
